import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, first, forkJoin, from, map, of, switchMap } from 'rxjs';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import * as fromModels from '../../modules/models/common-model';
import { Utils } from '../../modules/shared/utils';
import { CommonDetailsService } from '../../modules/shared/common-details.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DynamicCrmService } from '../../modules/services/dynamic-crm.service';
import { ICrmNote } from '../../modules/models/dynamic-crm-models';
import { hasRichTextContent, sanitizeTrustedHtml } from '../../modules/shared/ckeditor/utils/rich-text.utils';
import { TradeDetailsTasksComponent } from './trade-details-tasks.component';
import { TradeDetailsInteractionsComponent } from './trade-details-interactions.component';
import { catchError } from 'rxjs/operators';

type DrawerDisplayFieldType = 'clientName' | 'accountNumber' | 'departmentName';

interface DrawerDisplayRule {
  fieldType: DrawerDisplayFieldType;
  reportTypes: string[];
  reportCodes?: string[];
}

interface DrawerDisplayConfig {
  defaultFieldType: DrawerDisplayFieldType;
  rules: DrawerDisplayRule[];
}

@Component({
  selector: 'app-trade-details',
  templateUrl: './trade-details.component.html',
  styleUrls: ['./trade-details.component.scss'],
  standalone: false
})
export class TradeDetails implements OnInit, AfterViewInit {

  private static attachmentCounter = 0;

  activeTab: 'account-activity' | 'd365-notes' | 'd365-tasks' | 'interactions' = 'account-activity';
  reportType = '';
  reportTypeValue = '';

  // Account Activity
  orderByField = '';
  orderBy = '';
  displayedColumns: fromModels.ColumnDef[] = [];

  get displayedColumnNames(): string[] {
    return this.displayedColumns.map(c => c.field);
  }

  trades: any[] = [];
  accountNumber = '';
  clientName = '';
  accountActivityLabel = 'Account Number';
  accountActivityValue = '';
  accountActivityFieldType: DrawerDisplayFieldType = 'accountNumber';
  partyDetails: { partyId: any; partyType: any; clientName?: string }[] = [];

  // D365 Notes — list
  crmNotes: ICrmNote[] = [];
  d365TasksCount = 0;
  d365InteractionsCount = 0;
  hasLoadedNotesCount = false;
  hasLoadedTasksCount = false;
  hasLoadedInteractionsCount = false;
  private isCrmEntityResolutionComplete = false;
  private hasResolvableCrmEntity = false;
  readonly isLoadingNotes$ = new BehaviorSubject<boolean>(false);
  notesLoadErrorMessage = '';
  notesSortOrder: 'asc' | 'desc' = 'desc';
  readonly isDownloadingAttachment$ = new BehaviorSubject<boolean>(false);
  noteAttachmentErrorMessage = '';

  // D365 Notes — "See more" overflow detection
  @ViewChildren('noteBodyEl') noteBodyElements?: QueryList<ElementRef<HTMLElement>>;

  // D365 Notes — add note form
  @ViewChild('addNoteAttachmentInput') addNoteAttachmentInput?: ElementRef<HTMLInputElement>;
  @ViewChild(TradeDetailsTasksComponent) tasksComponent?: TradeDetailsTasksComponent;
  @ViewChild(TradeDetailsInteractionsComponent) interactionsComponent?: TradeDetailsInteractionsComponent;
  isAddNoteOpen = false;
  isSubmittingNote = false;
  addNoteErrorMessage = '';
  addNoteSuccessMessage = '';
  selectedAttachments: Array<{
    id: string;
    name: string;
    size: number;
    mimeType: string;
    documentBody: string;
  }> = [];

  addNoteForm = new FormGroup({
    noteTitle: new FormControl<string>('', Validators.required),
    noteBody: new FormControl<string>('', (control: AbstractControl): ValidationErrors | null => {
      return hasRichTextContent(control.value || '') ? null : { required: true };
    })
  });

  constructor(
    public dialogRef: MatDialogRef<TradeDetails>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commonDetailsService: CommonDetailsService,
    private dynamicCrmSvc: DynamicCrmService,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.trades = this.data.row?.tradeDetails || [];
    this.displayedColumns = this.data.displayedColumns || [];
    this.accountNumber = this.data.accountNumber || '';
    this.clientName = this.data.clientName || '';
    this.partyDetails = this.data.partyDetails || [];
    this.reportType = this.data.reportType || '';
    this.reportTypeValue = this.data.reportTypeValue || '';
    this.accountActivityValue = this.accountNumber;
  }

  ngOnInit(): void {
    this.loadAccountActivityDisplayConfig();
    // Preload counts so tab badges are visible when the panel opens.
    this.preloadTabCounts();
  }

  ngAfterViewInit(): void {
    this.noteBodyElements?.changes.subscribe(() => this.scheduleNoteOverflowDetection());
  }

  private loadAccountActivityDisplayConfig(): void {
    this.httpClient
      .get<DrawerDisplayConfig>('assets/config/response-drawer-display-config.json')
      .pipe(catchError(() => of({ defaultFieldType: 'accountNumber', rules: [] } as DrawerDisplayConfig)))
      .subscribe(config => {
        this.accountActivityFieldType = this.resolveDisplayFieldType(config);
        this.accountActivityLabel = this.getDisplayLabel(this.accountActivityFieldType);
        this.accountActivityValue = this.resolveDisplayValue(this.accountActivityFieldType) || this.accountNumber;
        this.cdr.detectChanges();
      });
  }

  private resolveDisplayFieldType(config: DrawerDisplayConfig): DrawerDisplayFieldType {
    const reportTypes = [this.reportType, this.reportTypeValue]
      .map(value => (value || '').toLowerCase().replace(/[^a-z0-9]/g, ''))
      .filter(Boolean);
    const reportCodes = [this.reportType, this.reportTypeValue]
      .map(value => (value || '').toUpperCase().trim())
      .filter(Boolean);
    for (const rule of config.rules || []) {
      const matchesType = (rule.reportTypes || []).some(type =>
        reportTypes.includes(type.toLowerCase().replace(/[^a-z0-9]/g, '')));
      const matchesCode = (rule.reportCodes || []).some(code => reportCodes.includes(code.toUpperCase().trim()));
      if (matchesType || matchesCode) {
        return rule.fieldType;
      }
    }
    return config.defaultFieldType || 'accountNumber';
  }

  private getDisplayLabel(fieldType: DrawerDisplayFieldType): string {
    if (fieldType === 'clientName') {
      return 'Client Name';
    }
    if (fieldType === 'departmentName') {
      return 'Department Name';
    }
    return 'Account Number';
  }

  private resolveDisplayValue(fieldType: DrawerDisplayFieldType): string {
    const row = this.data.row || {};
    const keys = fieldType === 'clientName'
      ? ['ClientName', 'clientName', 'CLIENTNAME', 'client_name']
      : fieldType === 'departmentName'
        ? ['TeamName', 'teamName', 'departmentName', 'department_name', 'departmentId']
        : ['accountNumber', 'account_number'];
    for (const key of keys) {
      const value = row[key];
      if (value !== undefined && value !== null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private preloadTabCounts(): void {
    const partiesForLookup = this.partyDetails.filter(p => !!p.partyId);
    if (!partiesForLookup.length) {
      this.isCrmEntityResolutionComplete = true;
      this.hasResolvableCrmEntity = false;
      this.cdr.detectChanges();
      return;
    }

    this.isCrmEntityResolutionComplete = false;
    this.hasResolvableCrmEntity = false;
    forkJoin(partiesForLookup.map(p =>
      this.dynamicCrmSvc.resolveCrmEntityByParty(p.partyId, p.partyType || '').pipe(first())
    )).pipe(first()).subscribe({
      next: results => {
        this.ngZone.run(() => {
          this.hasResolvableCrmEntity = results.some(result => !!result.crmId && !!result.crmEntityType);
          this.isCrmEntityResolutionComplete = true;
          this.cdr.detectChanges();
          if (this.hasResolvableCrmEntity) {
            this.preloadNotesCount();
            this.preloadTasksCount();
            this.preloadInteractionsCount();
          }
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.isCrmEntityResolutionComplete = true;
          this.hasResolvableCrmEntity = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  private preloadNotesCount(): void {
    this.dynamicCrmSvc.getNotes(this.partyDetails, this.notesSortOrder)
      .pipe(first())
      .subscribe({
        next: notes => {
          this.ngZone.run(() => {
            this.crmNotes = notes;
            this.hasLoadedNotesCount = true;
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.crmNotes = [];
            this.hasLoadedNotesCount = true;
            this.cdr.detectChanges();
          });
        }
      });
  }

  private preloadTasksCount(): void {
    const partiesForLookup = this.partyDetails.filter(p => !!p.partyId);
    if (!partiesForLookup.length) {
      this.d365TasksCount = 0;
      this.hasLoadedTasksCount = true;
      return;
    }

    const resolvers = partiesForLookup.map(p =>
      this.dynamicCrmSvc.resolveCrmEntityByParty(p.partyId, p.partyType || '').pipe(first())
    );

    forkJoin(resolvers)
      .pipe(
        first(),
        switchMap(results => {
          const profileIds = results
            .map(r => r.crmId)
            .filter((id): id is string => !!id);

          if (!profileIds.length) {
            return of([]);
          }

          return this.dynamicCrmSvc.getTasksByProfiles({
            profileIds,
            sortOrder: 'desc',
            timeRange: 'all'
          }).pipe(first());
        })
      )
      .subscribe({
        next: tasks => {
          this.ngZone.run(() => {
            this.d365TasksCount = tasks.length;
            this.hasLoadedTasksCount = true;
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.d365TasksCount = 0;
            this.hasLoadedTasksCount = true;
            this.cdr.detectChanges();
          });
        }
      });
  }

  private preloadInteractionsCount(): void {
    const partiesForLookup = this.partyDetails.filter(p => !!p.partyId);
    if (!partiesForLookup.length) {
      this.d365InteractionsCount = 0;
      this.hasLoadedInteractionsCount = true;
      return;
    }

    const resolvers = partiesForLookup.map(p =>
      this.dynamicCrmSvc.resolveCrmEntityByParty(p.partyId, p.partyType || '').pipe(first())
    );

    forkJoin(resolvers)
      .pipe(
        first(),
        switchMap(results => {
          const profileIds = results
            .map(r => r.crmId)
            .filter((id): id is string => !!id);

          if (!profileIds.length) {
            return of([]);
          }

          return this.dynamicCrmSvc.getInteractionsByProfiles({
            profileIds,
            sortOrder: 'desc'
          }).pipe(first());
        })
      )
      .subscribe({
        next: interactions => {
          this.ngZone.run(() => {
            this.d365InteractionsCount = interactions.length;
            this.hasLoadedInteractionsCount = true;
            this.cdr.detectChanges();
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.d365InteractionsCount = 0;
            this.hasLoadedInteractionsCount = true;
            this.cdr.detectChanges();
          });
        }
      });
  }

  setTab(tab: 'account-activity' | 'd365-notes' | 'd365-tasks' | 'interactions'): void {
    if (tab === 'd365-notes' && this.isD365NotesDisabled()) {
      return;
    }

    if (tab === 'd365-tasks' && this.isD365TasksDisabled()) {
      return;
    }

    if (tab === 'interactions' && this.isInteractionsDisabled()) {
      return;
    }

    this.activeTab = tab;
    if (tab === 'd365-notes') {
      this.loadNotes();
      return;
    }

    if (tab === 'd365-tasks') {
      setTimeout(() => this.tasksComponent?.getTasks(), 0);
      return;
    }

    if (tab === 'interactions') {
      setTimeout(() => this.interactionsComponent?.getInteractions(), 0);
    }
  }

  onTasksCountChange(count: number): void {
    this.d365TasksCount = count;
    this.hasLoadedTasksCount = true;
  }

  onInteractionsCountChange(count: number): void {
    this.d365InteractionsCount = count;
    this.hasLoadedInteractionsCount = true;
  }

  isD365TasksDisabled(): boolean {
    return !this.hasPartyDetails();
  }

  isInteractionsDisabled(): boolean {
    return !this.hasPartyDetails();
  }

  isD365NotesDisabled(): boolean {
    return !this.hasPartyDetails();
  }

  get unsupportedReportMessage(): string {
    return 'D365 Notes, Tasks, and Interactions are not available because this report does not have client information.';
  }

  private hasPartyDetails(): boolean {
    return this.isCrmEntityResolutionComplete && this.hasResolvableCrmEntity;
  }

  // ── Account Activity ───────────────────────────────────────────────────────

  applyOrderBy(field: string, isNumber = false) {
    if (field === this.orderByField) {
      this.orderBy = this.orderBy === 'DESC' ? 'ASC' : 'DESC';
    } else {
      this.orderBy = 'DESC';
    }
    this.orderByField = field;
    this.trades = Utils.applyOrderBy(this.trades, this.orderBy, this.orderByField, isNumber);
  }

  getHeaderTextBasedondata(element: any): string {
    if (element?.side === 'B' || element?.side === 'S') return 'Buy/Sell';
    if (element?.side === 'A' || element?.side === 'H') return 'Contract Status';
    return 'Unknown';
  }

  close() {
    this.dialogRef.close();
  }

  formatValue(value: any, col: fromModels.ColumnDef): string {
    return this.commonDetailsService.formatValue(value, col);
  }

  formatSubField(value: any, col: fromModels.ColumnDef): string {
    if (!col.subField) return value;
    return this.commonDetailsService.formatByDataType(value, col.subFieldUiDataType);
  }

  isTimestamp(col: fromModels.ColumnDef): boolean {
    return (col.subFieldUiDataType || '').toLowerCase() === 'timestamp';
  }

  getAlignment(col: fromModels.ColumnDef): string {
    return this.commonDetailsService.getAlignment(col, this.trades);
  }

  // ── D365 Notes ─────────────────────────────────────────────────────────────

  loadNotes(): void {
    if (!this.partyDetails.length) return;
    this.setNotesLoading(true);
    this.notesLoadErrorMessage = '';
    this.noteAttachmentErrorMessage = '';
    this.dynamicCrmSvc.getNotes(this.partyDetails, this.notesSortOrder)
      .pipe(first())
      .subscribe({
        next: notes => {
          this.ngZone.run(() => {
            this.crmNotes = notes;
            this.hasLoadedNotesCount = true;
            this.setNotesLoading(false);
            this.cdr.detectChanges();
            this.scheduleNoteOverflowDetection();
          });
        },
        error: () => {
          this.ngZone.run(() => {
            this.notesLoadErrorMessage = 'Failed to load notes.';
            this.setNotesLoading(false);
            this.cdr.detectChanges();
          });
        }
      });
  }

  toggleNoteSort(): void {
    this.notesSortOrder = this.notesSortOrder === 'desc' ? 'asc' : 'desc';
    this.loadNotes();
  }

  toggleNoteExpand(note: ICrmNote): void {
    note.isExpanded = !note.isExpanded;
  }

  trackNoteById(_index: number, note: ICrmNote): string {
    return note.annotationId;
  }

  sanitizeNoteHtml(html: string | undefined): SafeHtml {
    if (!html) return '';
    return sanitizeTrustedHtml(this.sanitizer, html);
  }

  // Runs after layout so the clamped height is measurable; only shows "See more" when the text overflows.
  private scheduleNoteOverflowDetection(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => this.detectNoteOverflow()));
  }

  private detectNoteOverflow(): void {
    if (!this.noteBodyElements?.length) return;
    this.noteBodyElements.forEach((ref, idx) => {
      const note = this.crmNotes[idx];
      if (!note || note.isExpanded) return;
      const el = ref.nativeElement;
      note.showSeeMore = el.scrollHeight - el.clientHeight > 1;
    });
    this.cdr.detectChanges();
  }

  getNoteInitials(ownerName: string | undefined): string {
    if (!ownerName?.trim()) return '?';
    const parts = ownerName.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  formatNoteDate(isoDate: string | undefined): string {
    if (!isoDate) return '';
    try {
      const d = new Date(isoDate);
      return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        + ' '
        + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return isoDate;
    }
  }

  getRegardingName(note: ICrmNote): string {
    return note.contactFullName || note.accountName || '';
  }

  toggleAddNote(): void {
    this.isAddNoteOpen = !this.isAddNoteOpen;
    if (!this.isAddNoteOpen) {
      this.addNoteForm.reset();
      this.clearAttachments();
      this.addNoteErrorMessage = '';
      this.addNoteSuccessMessage = '';
    }
  }

  onNoteBodyChanged(event: { html: string; editor: any }): void {
    this.addNoteForm.get('noteBody')?.setValue(event.html);
    this.addNoteForm.get('noteBody')?.markAsTouched();
  }

  openAttachmentPicker(): void {
    if (this.isSubmittingNote) return;
    this.addNoteAttachmentInput?.nativeElement.click();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input?.files || []);
    if (!files.length) {
      if (input) {
        input.value = '';
      }
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    const emptyFile = files.find(file => file.size === 0);
    if (emptyFile) {
      this.addNoteErrorMessage = 'Attachment cannot be empty.';
      if (input) {
        input.value = '';
      }
      return;
    }
    const oversized = files.find(file => file.size > maxSize);
    if (oversized) {
      this.addNoteErrorMessage = 'Attachment must be 5 MB or less.';
      if (input) {
        input.value = '';
      }
      return;
    }
    this.addNoteErrorMessage = '';

    // Reset native input text so filename is shown only in the custom metadata row.
    if (input) {
      input.value = '';
    }

    const pendingAttachments = files.map(file => ({
      id: this.buildAttachmentId(file),
      name: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      documentBody: ''
    }));

    // Single-file mode: keep only the most recently selected file.
    const latestAttachment = pendingAttachments[pendingAttachments.length - 1];
    this.selectedAttachments = [latestAttachment];

    const latestFile = files[files.length - 1];
    const reads$ = [
      from(this.readFileAsBase64(latestFile)).pipe(
        map((base64: string) => ({ id: latestAttachment.id, base64 }))
      )
    ];

    forkJoin(reads$).pipe(first()).subscribe({
      next: payloads => {
        const bodyMap = new Map<string, string>(payloads.map(p => [p.id, p.base64] as [string, string]));
        this.selectedAttachments = this.selectedAttachments.map(attachment =>
          bodyMap.has(attachment.id)
            ? { ...attachment, documentBody: bodyMap.get(attachment.id) || '' }
            : attachment
        );
      },
      error: () => {
        const pendingIds = new Set([latestAttachment.id]);
        this.selectedAttachments = this.selectedAttachments.filter(a => !pendingIds.has(a.id));
        this.addNoteErrorMessage = 'Unable to read the selected file.';
      }
    });
  }

  removeAttachment(attachmentId: string): void {
    this.selectedAttachments = this.selectedAttachments.filter(a => a.id !== attachmentId);
    if (this.addNoteAttachmentInput?.nativeElement) {
      this.addNoteAttachmentInput.nativeElement.value = '';
    }
  }

  clearAttachments(resetInput = true): void {
    this.selectedAttachments = [];
    if (resetInput && this.addNoteAttachmentInput?.nativeElement) {
      this.addNoteAttachmentInput.nativeElement.value = '';
    }
  }

  formatAttachmentSize(bytes: number): string {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  submitNote(): void {
    if (this.addNoteForm.invalid || this.isSubmittingNote) return;
    const party = this.partyDetails[0];
    if (!party) {
      this.addNoteErrorMessage = 'No party information available to link this note.';
      return;
    }
    const { noteTitle, noteBody } = this.addNoteForm.value;
    if (!noteTitle?.trim() || !noteBody?.trim()) {
      this.addNoteForm.markAllAsTouched();
      return;
    }

    if (this.selectedAttachments.some(a => !a.documentBody)) {
      this.addNoteErrorMessage = 'Please wait for attachments to finish loading before saving.';
      return;
    }

    this.isSubmittingNote = true;
    this.addNoteErrorMessage = '';
    this.addNoteSuccessMessage = '';

    const title = noteTitle.trim();
    const body = noteBody.trim();
    const attachment = this.selectedAttachments[0];

    console.debug('[TradeDetails] submitNote started', {
      partyId: party.partyId,
      partyType: party.partyType,
      hasAttachment: !!attachment,
      attachmentName: attachment?.name,
      attachmentSize: attachment?.size,
      title,
      bodyLength: body.length
    });

    const submit$ = !attachment
      ? this.dynamicCrmSvc.addNote(party.partyId, party.partyType, { title, body })
      : this.dynamicCrmSvc.addNote(party.partyId, party.partyType, {
          title,
          body,
          filename: attachment.name,
          mimetype: attachment.mimeType,
          documentbody: attachment.documentBody
        });

    submit$.pipe(first()).subscribe({
      next: response => {
        console.debug('[TradeDetails] submitNote success', {
          partyId: party.partyId,
          partyType: party.partyType,
          response
        });
        this.isSubmittingNote = false;
        this.isAddNoteOpen = false;
        this.addNoteForm.reset();
        this.clearAttachments();
        this.addNoteSuccessMessage = 'Note added successfully.';
        this.loadNotes();
        setTimeout(() => this.loadNotes(), 750);
      },
      error: error => {
        console.error('[TradeDetails] submitNote failed', {
          partyId: party.partyId,
          partyType: party.partyType,
          hasAttachment: !!attachment,
          error
        });
        this.isSubmittingNote = false;
        this.addNoteErrorMessage = 'Failed to add note. Please try again.';
      }
    });
  }

  downloadNoteAttachment(note: ICrmNote): void {
    const annotationId = note?.annotationId;
    if (!annotationId || this.isDownloadingAttachment$.value) {
      return;
    }

    this.noteAttachmentErrorMessage = '';
    this.isDownloadingAttachment$.next(true);

    this.dynamicCrmSvc.getAttachmentDownloadData(annotationId).pipe(first()).subscribe({
      next: downloadData => {
        this.isDownloadingAttachment$.next(false);
        if (!downloadData) {
          this.noteAttachmentErrorMessage = 'Attachment is not available for download.';
          return;
        }

        const blob = new Blob([downloadData.bytes], { type: downloadData.mimetype });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = downloadData.filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isDownloadingAttachment$.next(false);
        this.noteAttachmentErrorMessage = 'Failed to download attachment. Please try again.';
      }
    });
  }

  private setNotesLoading(value: boolean): void {
    // Defers binding updates to avoid NG0100 during fast tab-switch/request cycles.
    setTimeout(() => {
      this.isLoadingNotes$.next(value);
    }, 0);
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = ((reader.result as string) || '').split(',')[1] || '';
        if (!base64) { reject(new Error('Attachment conversion failed')); return; }
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsDataURL(file);
    });
  }

  private buildAttachmentId(file: File): string {
    TradeDetails.attachmentCounter += 1;
    return `${file.name}_${file.size}_${file.lastModified}_${TradeDetails.attachmentCounter}`;
  }
}
